// -----------------------------------------------------------------------------
// files.ts — stored files (v0.6.0 Part 6): object names, shrinking pictures, reading dropped / pasted images.
//
//   Object names (bucket "files"; the first folder is the project — storage rules in 0015_files.sql):
//     <project id>/snapshots/<uuid>.jpg     <project id>/plans/<uuid>.pdf
//   Snapshots are shrunk in the browser to about 300 KB (JPEG, longest side ≤ 2000 px) before upload,
//   so 1 GB (Supabase Free) holds roughly 3,000 snapshots.
//   The original file name is kept in app.files.name; the viewer shows "GF-03_snapshot_02.jpg".
// Used by: modules/issues/components/Snapshots.tsx, IssuePage (📎), AddIssueDrawer, SetupPage (A6.7 plan upload).
// Pure parts are unit-tested in tests/unit/files.test.ts.
// -----------------------------------------------------------------------------
import { files } from "./api/backend";

export type FileKind = "snapshots" | "plans" | "cover";
export const SNAPSHOT_TARGET = 300 * 1024;
export const SNAPSHOT_MAX_SIDE = 2000;
export const PLAN_MAX = 20 * 1024 * 1024;
export const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

const uuid = () => (globalThis.crypto?.randomUUID?.() ?? "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
  (Number(c) ^ (Math.random() * 16) >> (Number(c) / 4)).toString(16)));

/** New object name for a file of this project. */
export const newPath = (projectId: string, kind: FileKind) => `${projectId}/${kind}/${uuid()}.${kind === "plans" ? "pdf" : "jpg"}`;

/** "GF-03_snapshot_02.jpg" — the name the viewer shows (n counts from 1). */
export const snapshotLabel = (code: string, n: number) => `${code}_snapshot_${String(n).padStart(2, "0")}.jpg`;

/** Size that keeps the picture's shape with the longest side ≤ max (never enlarges). */
export function fitSize(w: number, h: number, max = SNAPSHOT_MAX_SIDE): { w: number; h: number } {
  const k = Math.min(1, max / Math.max(w, h, 1));
  return { w: Math.max(1, Math.round(w * k)), h: Math.max(1, Math.round(h * k)) };
}

/** Next try when a JPEG is still too big: lower the quality first (down to 0.5), then shrink the size by 20 %. */
export function nextTry(t: { q: number; scale: number }): { q: number; scale: number } {
  return t.q > 0.55 ? { q: Math.round((t.q - 0.1) * 100) / 100, scale: t.scale } : { q: 0.8, scale: t.scale * 0.8 };
}

/** "3.1 MB", "280 KB" */
export function formatBytes(n: number): string {
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(2)} GB`;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}

/** The first picture in a drop or a paste (DataTransfer from DragEvent.dataTransfer / ClipboardEvent.clipboardData). */
/** v0.9.1 (round 32 #10): every picture in a drop / paste (JPEG, PNG, WebP), in the order given. */
export function allImages(dt: { files?: ArrayLike<File> | null } | null | undefined): File[] {
  return Array.from(dt?.files ?? []).filter((f) => IMAGE_TYPES.includes(f.type));
}
export function firstImage(dt: { files?: ArrayLike<File> | null } | null | undefined): File | null {
  const list = Array.from(dt?.files ?? []);
  return list.find((f) => IMAGE_TYPES.includes(f.type)) ?? null;
}

/** Shrink a picture to a JPEG of about 300 KB (browser only: canvas). */
export async function shrinkImage(file: Blob): Promise<Blob> {
  if (!IMAGE_TYPES.includes(file.type)) throw new Error("Pick a JPEG, PNG or WebP picture");
  const bmp = await createImageBitmap(file);
  const base = fitSize(bmp.width, bmp.height);
  let t = { q: 0.85, scale: 1 }, out: Blob | null = null;
  for (let i = 0; i < 12; i++) {
    const c = document.createElement("canvas");
    c.width = Math.max(1, Math.round(base.w * t.scale)); c.height = Math.max(1, Math.round(base.h * t.scale));
    const g = c.getContext("2d")!;
    g.fillStyle = "#ffffff"; g.fillRect(0, 0, c.width, c.height);                 // PNG transparency → white, like paper
    g.drawImage(bmp, 0, 0, c.width, c.height);
    out = await new Promise<Blob | null>((ok) => c.toBlob(ok, "image/jpeg", t.q));
    if (out && out.size <= SNAPSHOT_TARGET) break;
    t = nextTry(t);
  }
  bmp.close?.();
  if (!out) throw new Error("This picture could not be read");
  return out;
}

/** Shrink + upload a snapshot; returns what issues_snapshot_add needs. */
export async function uploadSnapshot(projectId: string, file: File) {
  const blob = await shrinkImage(file);
  const path = newPath(projectId, "snapshots");
  await files.upload(path, blob, "image/jpeg");
  return { path, name: file.name || "snapshot.jpg", bytes: blob.size, mime: "image/jpeg" };
}

/** Upload a plan PDF (≤ 20 MB); returns what plan_add needs. */
/** v0.7.0: a cover picture (data URL from Project setup) → storage "<project>/cover/<uuid>.jpg|png"; returns its object name. */
export async function uploadCover(projectId: string, dataUrl: string): Promise<string> {
  const blob = await (await fetch(dataUrl)).blob();
  const png = blob.type === "image/png";
  const path = `${projectId}/cover/${uuid()}.${png ? "png" : "jpg"}`;
  await files.upload(path, blob, png ? "image/png" : "image/jpeg");
  return path;
}

export async function uploadPlan(projectId: string, file: File) {
  if (file.type !== "application/pdf" && !/\.pdf$/i.test(file.name)) throw new Error("Plans must be PDF files");
  if (file.size > PLAN_MAX) throw new Error(`This PDF is ${formatBytes(file.size)} — plans must be 20 MB or less`);
  const path = newPath(projectId, "plans");
  await files.upload(path, file, "application/pdf");
  return { path, name: file.name, bytes: file.size, mime: "application/pdf" };
}
